import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { translate } from "react-i18next";
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  connect,
} from "twilio-video";
import { connect as reduxConnect } from "react-redux";
import config from "config";

// Components
import ExtComp from "../external";
import Timer from "./Timer";

// Style && Images
import s from "./VideoBlock.style";
import {
  UserPhotoDefault,
  CamIcon,
  CamInActiveIcon,
  MicIcon,
  MicInActiveIcon,
  ChatIcon,
  ChatInActiveIcon,
  ScreenIcon,
  ScreenInActiveIcon,
  InfoIcon,
  InfoInActiveIcon,
  DoctorPhotoDefault,
} from "../svgIcons";

// Services
import { alertError } from "src/services/alerts";
import { http } from "src/services/http";

const UserPreview = (props) => {
  const { patient, onConnect, waiting, isPatientReady, i18n } = props;
  return (
    <div className="patient-details">
      <ExtComp.ImageWithDefault
        alt="patient"
        image={patient && patient.photo}
        defaultImg={UserPhotoDefault}
      />
      <h3 className="user-name">
        <span className="last-name">{patient && patient.lastName} </span>
        <span>{patient && patient.firstName}</span>
      </h3>
      {isPatientReady && <p className="user-status-succes" >{i18n.t('patient_ready_status_message')}</p>}
      {!isPatientReady && <p className="user-status-fails">{i18n.t('patient_not_ready_status_message')}</p>}
      <p className="user-waiting" />

      {onConnect ? (
        <button className="flat-button start-consultation" onClick={onConnect}>
          {i18n.t('start_video_consultation')}
        </button>
      ) : null}
      {waiting ? <h3>{i18n.t('waiting_for_patient')}</h3> : null}
    </div>
  );
};


const canScreenShare = () => {
  function isFirefox() {
    var mediaSourceSupport = !!navigator.mediaDevices.getSupportedConstraints().mediaSource;
    // TODO: check regex and test firefox
    var matchData = navigator.userAgent.match(/Firefox/(d));
    var firefoxVersion = 0;
    if (matchData && matchData[1]) {
      firefoxVersion = parseInt(matchData[1], 10);
    }
    return mediaSourceSupport && firefoxVersion >= 52;
  }

  function isChrome() {
    return 'chrome' in window;
  }

  return isFirefox() || isChrome();
}

const VideoBlock = (props) => {
  const {
    activeAppointment,
    doctor,
    patient,
    showDrawer,
    triggerDrawer,
    togglePolling,
    t,
    i18n,
    isPatientReady,
  } = props;
  const [room, setRoom] = useState(null);
  const [participantConnected, setParticipantConnected] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [publications, setPublications] = useState([]);
  const [timeStamp, setTimeStamp] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null);

  const cameraParticipantRef = useRef(null);
  const micParticipantRef = useRef(null);
  const localVideoRef = useRef(null);
  const localAudioRef = useRef(null);

  const triggerVideo = () => {
    if (localVideoTrack) {
      localVideoTrack.isEnabled
        ? localVideoTrack.disable()
        : localVideoTrack.enable();
    }
    setTimeStamp(new Date().getTime());
  };

  const triggerAudio = () => {
    if (localAudioTrack) {
      localAudioTrack.isEnabled
        ? localAudioTrack.disable()
        : localAudioTrack.enable();
    }
    setTimeStamp(new Date().getTime());
  };

  const shareScreen = async () => {
    if (!isScreenSharing) {
      const screen = await navigator.mediaDevices.getDisplayMedia();
      const stream = screen.getVideoTracks()[0];
      setScreenTrack(stream);

      if (room && room.localParticipant) {
        room.localParticipant.publishTrack(stream, { name: 'screen-share' });

        // Default stop sharing button
        stream.addEventListener('ended', (e) => {
          room.localParticipant.unpublishTrack(e.target);
        })
      }
    } else {
      if (room && room.localParticipant) {
        screenTrack.stop();
        room.localParticipant.unpublishTrack(screenTrack);
      }
    }

    setIsScreenSharing(!isScreenSharing);
  }

  const actionButtons = [
    {
      key: "cam",
      action: triggerVideo,
      disabled: !localVideoTrack,
      inactive: localVideoTrack && !localVideoTrack.isEnabled,
      icon: <CamIcon />,
      inactiveIcon: <CamInActiveIcon />,
      title: "Mute Video",
      inactiveTitle: "Unmute Video",
    },
    {
      key: "mic",
      action: triggerAudio,
      disabled: !localAudioTrack,
      inactive: localAudioTrack && !localAudioTrack.isEnabled,
      icon: <MicIcon />,
      inactiveIcon: <MicInActiveIcon />,
      title: "Mute Audio",
      inactiveTitle: "Unmute Audio",
    },
    {
      key: "chat",
      disabled: true,
      inactive: true,
      icon: <ChatIcon />,
      inactiveIcon: <ChatInActiveIcon />,
    },
    {
      key: "screen",
      action: shareScreen,
      disabled: false,
      inactive: !isScreenSharing,
      icon: <ScreenIcon />,
      inactiveIcon: <ScreenInActiveIcon />,
      show: room ? true : false,
    },
    {
      key: "info",
      action: triggerDrawer,
      disabled: false,
      inactive: showDrawer,
      icon: <InfoIcon />,
      inactiveIcon: <InfoInActiveIcon />,
      title: "Open Details",
      inactiveTitle: "Hide Details",
    },
  ];

  useEffect(() => {
    createLocalVideoTrack({ name: "camera" }).then((track) => {
      setLocalVideoTrack(track);
      track.attach(localVideoRef.current);
    });
    createLocalAudioTrack({ name: "mic" }).then((track) => {
      setLocalAudioTrack(track);
      track.attach(localAudioRef.current);
    });
    return () => {
      if (localVideoTrack) localVideoTrack.stop();
      if (localAudioTrack) localAudioTrack.stop();
    };
  }, []);

  useEffect(
    () => {
      console.log("PUBLICATIONS ", publications);
      if (Array.isArray(publications)) {
        publications.forEach((publication) => {
          if (publication.kind === "video") {
            if (publication.isTrackEnabled) {
              console.log("TRYING TO ATTACH REMOTE CAMERA");
              publication.track.attach(cameraParticipantRef.current);
            } else {
              publication.track.detach(cameraParticipantRef.current);
            }
          }
          if (publication.kind === "audio") {
            if (publication.isTrackEnabled) {
              publication.track.attach(micParticipantRef.current);
            } else {
              publication.track.detach(micParticipantRef.current);
            }
          }
        });
      }
    },
    [publications]
  );

  const updatePublication = (prevPublication, publication) => {
    let newPublication = [];
    if (
      prevPublication.find((pub) => pub.trackName === publication.trackName)
    ) {
      newPublication =
        prevPublication.map(
          (pub) => (publication.trackName === pub.trackName ? publication : pub)
        ) || [];
    } else {
      newPublication = [...prevPublication, publication];
    }
    setPublications(newPublication);
  };

  function participantSubscription(participant) {
    participant.on("trackDisabled", (publication) => {
      setPublications((prevPublications) =>
        updatePublication(prevPublications, publication)
      );
    });
    participant.on("trackEnabled", (publication) => {
      setPublications((prevPublications) =>
        updatePublication(prevPublications, publication)
      );
    });
    participant.on("trackSubscribed", (track, publication) => {
      setPublications((prevPublications) =>
        updatePublication(prevPublications, publication)
      );
    });
  }

  const connectToRoom = async () => {
    // Stop polling for patient status
    togglePolling();

    if (activeAppointment && activeAppointment.id) {
      const response = await http.get(
        `${config.gateway}/video/token?appointmentId=${activeAppointment.id}`
      );
      const token = response.data;

      connect(
        token,
        { tracks: [] }
      ).then((room) => {

        room.localParticipant.publishTrack(localVideoTrack);
        room.localParticipant.publishTrack(localAudioTrack);

        // Log any Participants already connected to the Room
        room.participants.forEach((participant) => {
          console.log("IN ROOM Participants");
          setParticipantConnected(true);
          participantSubscription(participant);
        });

        // Log new Participants as they connect to the Room
        room.once("participantConnected", (participant) => {
          console.log("New Participants connect");
          setParticipantConnected(true);
          participantSubscription(participant);
        });

        // Log Participants as they disconnect from the Room
        room.once("participantDisconnected", (participant) => {
          setParticipantConnected(false);
          setPublications([]);
          console.log(`Participant "${participant.identity}" DISCONNECTED`);
        });

        room.on("disconnected", (room, error) => {
          console.log("error ", error);
          setRoom(null);
          setPublications([]);
          setParticipantConnected(false);
          if (error) {
            alertError(error.message);
          }
        });

        setRoom(room);
      });
    }
  };

  const finishConsultation = () => {
    if (room) {
      room.disconnect();

      // Start polling patient status again
      togglePolling();
    }
  };

  const renderPreview = () => {
    if (room) {
      if (participantConnected) {
        let publicationDisabledMic;
        let publicationDisabledCamera;
        if (Array.isArray(publications)) {
          publications.forEach((publication) => {
            if (
              publication.trackName === "camera" &&
              !publication.isTrackEnabled
            )
              publicationDisabledCamera = true;
            if (publication.trackName === "mic" && !publication.isTrackEnabled)
              publicationDisabledMic = true;
          });
        }

        return (
          <div className="participant-video-wrapper">
            <div className="participant-video-status">
              {publicationDisabledMic ? <MicInActiveIcon /> : null}
              {publicationDisabledCamera ? <CamInActiveIcon /> : null}
            </div>
            <video className="participant-video" ref={cameraParticipantRef} />
            <audio ref={micParticipantRef} />
          </div>
        );
      } else {
        return <UserPreview patient={patient} waiting i18n={i18n} />;
      }
    } else {
      return <UserPreview patient={patient} onConnect={connectToRoom} isPatientReady={isPatientReady} i18n={i18n} />;
    }
  };

  return (
    <s.VideoBlock>
      {/* ──── Header ──────────────────────────────────────────────────────────────────────────── */}
      <header className="header">
        <div className="logo-wrapper">{global.theme.shortLogo}</div>
        <h2 className="screen-title">{i18n.t('video_consultation')}</h2>
        {publications && publications.length ? <Timer /> : null}
      </header>
      {/* ──── Patient info ────────────────────────────────────────────────────────────────────── */}

      {renderPreview()}

      {/* ──── Footer with buttons ─────────────────────────────────────────────────────────────── */}

      <footer className="footer">
        {localVideoTrack ? (
          <video ref={localVideoRef} className="local-video-track" autoPlay />
        ) : (
            <ExtComp.ImageWithDefault
              defaultImg={DoctorPhotoDefault}
              alt="doctor"
              image={doctor && doctor.photo}
            />
          )}
        <audio ref={localAudioRef} />

        <div className="buttons-row">
          {actionButtons.filter(b => b.show !== false).map((b) => (
            <button
              className="action-button"
              key={b.key}
              onClick={b.action}
              disabled={b.disabled}
              title={t(b.title)}
            >
              {b.inactive || b.disabled ? b.inactiveIcon : b.icon}
            </button>
          ))}
        </div>

        {room && (
          <button
            className="flat-button end-call-button"
            onClick={finishConsultation}
          >
            Finish Consultation
          </button>
        )}
      </footer>
    </s.VideoBlock>
  );
};
VideoBlock.propTypes = {
  doctor: PropTypes.shape(),
  patient: PropTypes.shape(),
  showDrawer: PropTypes.bool,
  t: PropTypes.func.isRequired,
  triggerDrawer: PropTypes.func,
};
VideoBlock.defaultProps = {
  doctor: null,
  patient: null,
  showDrawer: false,
  triggerDrawer: null,
};

const mapStateToProps = (store) => ({
  activeAppointment: store.practitioner.appointment.activeAppointment,
});

export default translate()(reduxConnect(mapStateToProps)(VideoBlock));
